/* eslint-disable react/jsx-props-no-spreading */
import type React from 'react';
import { Box } from '@doda/io-react/common';
import type { JobSearchResultResponse } from '@doda/api-client/aspida/@types';
import { SimilarKeywordAreaType } from 'components/JobCard';
import { SimilarKeywordArea } from 'components/JobCard/parts/SimilarKeywordArea';
import { MatchingBalloon } from '../MatchingBalloon';
import { ViewedBadge } from '../ViewedBadge';
import { Title } from '../Title';
import { SubTitle } from '../SubTitle';
import {
  ApplyButton,
  type ApplyEventHandler,
  type ApplyButtonProps,
} from '../ApplyButton';
import {
  EmphasizedTagList,
  KodawariTagList,
  type EmphasizedTagListProps,
  type KodawariTagListProps,
} from '../Tag';
import { SeeJobDetails } from '../SeeJobDetails';
import {
  FavoriteButton,
  type FavoriteButtonProps,
  type FavoriteProps,
} from '../FavoriteButton';
import styles from './index.module.scss';

/**
 * CardLayoutコンポーネントに渡されるプロパティを定義するインターフェース
 */
interface LayoutProps {
  /** 子要素 */
  children: React.ReactNode;
  /** 求人が歓迎されるかどうか */
  isWelcome: JobSearchResultResponse['isApplicationWelcome'];
  /** 求職が済んでいるかどうか */
  isApplied: JobSearchResultResponse['isApplied'];
  /** ブックマークに保存されているかどうか */
  isBookmarked: JobSearchResultResponse['isBookmarkSaved'];
}

/**
 * カードレイアウトを表示するコンポーネント
 *
 * @param {LayoutProps} props - プロパティ
 * @returns {JSX.Element} - レイアウト要素
 */
export const CardLayout = (props: LayoutProps) => {
  const {
    children,
    isWelcome = false,
    isApplied = false,
    isBookmarked = false,
  } = props;
  const isMatch = isWelcome && !isApplied && isBookmarked;

  return (
    <div>
      {isMatch && (
        <div className={styles['jobCard-matchingBalloonWrap']}>
          <MatchingBalloon />
        </div>
      )}
      <Box radius="4" boxShadow="blue" className={styles['jobCard-card']}>
        <article>{children}</article>
      </Box>
    </div>
  );
};

/**
 * Headerコンポーネントに渡されるプロパティを定義するインターフェース
 */
interface HeaderProps {
  /** 求人が閲覧済みかどうか */
  isRead: JobSearchResultResponse['isRead'];
  /** タイトル */
  title: string;
  /** サブタイトル */
  subTitle: string;
  /** 求人詳細ページのURL */
  hrefForJobDetail: string;
  /** 強調タグのプロパティ */
  emphasizedTag: EmphasizedTagListProps;
  /** こだわりタグのプロパティ */
  kodawariTag: KodawariTagListProps;
  /** 応募ボタンのプロパティ */
  applyButton: ApplyButtonProps;
  /** 類似キーワードエリアのプロパティ */
  similarKeywordArea?: SimilarKeywordAreaType | undefined;
  /** タイトルのナビゲートイベントハンドラ */
  onNavigateTitle?: React.MouseEventHandler<HTMLAnchorElement>;
  /** 直接応募のイベントハンドラ */
  onApplyDirect?: ApplyEventHandler;
  /** エージェント応募のイベントハンドラ */
  onApplyAgent?: ApplyEventHandler;
}

/**
 * 求人カードのヘッダー部分を表示するコンポーネント
 *
 * @param {HeaderProps} props - プロパティ
 * @returns {JSX.Element} - ヘッダー要素
 */
export const Header = (props: HeaderProps) => {
  const {
    isRead,
    title,
    subTitle,
    hrefForJobDetail,
    emphasizedTag,
    kodawariTag,
    applyButton,
    similarKeywordArea,
    onNavigateTitle,
    onApplyDirect,
    onApplyAgent,
  } = props;

  return (
    <header className={styles['jobCard-header']}>
      {isRead && (
        <div className={styles['jobCard-header__viewedBadge']}>
          <ViewedBadge />
        </div>
      )}
      <div className={styles['jobCard-header__container']}>
        <div className={styles['jobCard-header__titleArea']}>
          {/* eslint-disable-next-line react/jsx-no-target-blank */}
          <a
            href={hrefForJobDetail}
            onClick={onNavigateTitle}
            className={styles['jobCard-header__link']}
            target="_blank"
            rel="noopener"
          >
            <Title text={title} />
            <SubTitle text={subTitle} />
          </a>
          <div className={styles['jobCard-header__Tag']}>
            <EmphasizedTagList {...emphasizedTag} />
            <KodawariTagList {...kodawariTag} />
          </div>
        </div>
        <div className={styles['jobCard-header__buttonArea']}>
          <ApplyButton
            {...applyButton}
            onApplyDirect={onApplyDirect}
            onApplyAgent={onApplyAgent}
          />
        </div>
      </div>
      {similarKeywordArea && (
        <SimilarKeywordArea similarKeywordArea={similarKeywordArea} />
      )}
    </header>
  );
};

/**
 * GetPropsParamのインターフェース
 */
export interface GetPropsParam {
  /** 求人データ */
  job: JobSearchResultResponse;
}

/**
 * 求人データからHeaderのプロパティを作成する関数
 *
 * @param {GetPropsParam} param - 引数として求人データを含むオブジェクト
 * @returns {HeaderProps} - 作成されたHeaderのプロパティ
 */
export function getHeaderProps({ job }: GetPropsParam): HeaderProps {
  const {
    corporateName = '',
    occupationName = '',
    isRead,
    kodawari = {},
    hasDirectLink,
    directLink,
    jid = '',
    jobOfferKind = '',
    url = '',
  } = job;
  const { isNewArrival, isNearDeadline, ...otherKodawari } = kodawari;
  return {
    title: corporateName,
    subTitle: occupationName,
    isRead,
    emphasizedTag: {
      isNewArrival,
      isNearDeadline,
    },
    kodawariTag: otherKodawari,
    hrefForJobDetail: url,
    applyButton: {
      hasDirectLink,
      directLink,
      jid,
      jobOfferKind,
    },
  };
}

/**
 * Bodyコンポーネントに渡されるプロパティを定義するインターフェース
 */
interface BodyProps {
  /** 子要素 */
  children: React.ReactNode;
}

/**
 * 求人カードの本文部分を表示するコンポーネント
 *
 * @param {BodyProps} props - プロパティ
 * @returns {JSX.Element} - 本文要素
 */
export const Body = ({ children }: BodyProps) => {
  return <div className={styles['jobCard-body']}>{children}</div>;
};

/**
 * Footerコンポーネントに渡されるプロパティを定義するインターフェース
 */
interface FooterProps {
  /** 求人詳細ページのURL */
  hrefForJobDetail: string;
  /** お気に入りボタンのプロパティ */
  favoriteButton: FavoriteButtonProps;
  /** ブックマーク用のクエリストリング */
  bookmarkUrlQuery?: string | undefined;
  /** フッターボタンのナビゲートイベントハンドラ */
  onNavigateFooterButton?: React.MouseEventHandler<HTMLAnchorElement>;
  /** ブックマークのクリックイベントハンドラ */
  onBookmark?: FavoriteProps['onClickFavorite'];
  /** ブックマークリストへのナビゲートイベントハンドラ */
  onNavigateToBookmarkList?: FavoriteProps['onClickSaved'];
}

/**
 * 求人カードのフッター部分を表示するコンポーネントです。
 *
 * @param {FooterProps} props - プロパティ
 * @returns {JSX.Element} - フッター要素
 */
export const Footer = (props: FooterProps) => {
  const {
    hrefForJobDetail,
    favoriteButton,
    bookmarkUrlQuery,
    onNavigateFooterButton,
    onBookmark,
    onNavigateToBookmarkList,
  } = props;

  return (
    <Box color="gray">
      <footer className={styles['jobCard-footer']}>
        <div className={styles['jobCard-footer__seeJobDetails']}>
          <SeeJobDetails
            href={hrefForJobDetail}
            onNavigate={onNavigateFooterButton}
          />
        </div>
        <FavoriteButton
          {...favoriteButton}
          bookmarkUrlQuery={bookmarkUrlQuery}
          onBookmark={onBookmark}
          onNavigateToBookmarkList={onNavigateToBookmarkList}
        />
      </footer>
    </Box>
  );
};

/**
 * 求人データからFooterのプロパティを作成する関数
 *
 * @param {GetPropsParam} param - 引数として求人データを含むオブジェクト
 * @returns {FooterProps} - 作成されたFooterのプロパティ
 */
export function getFooterProps({ job }: GetPropsParam): FooterProps {
  const { jid = '', isBookmarkSaved, isApplied, url = '' } = job;
  return {
    hrefForJobDetail: url,
    favoriteButton: {
      jid,
      isBookmarked: isBookmarkSaved,
      isApplied,
    },
  };
}
